// ============================================
// Question distribution maths — shared by admin UI and server (OEL-70)
// ============================================
// The Automatic Pickup panel used to compute its displayed numbers with
// Math.round per band while the server picked with Math.floor + remainder, so
// the screen promised a split the generated paper never had (e.g. a 16-question
// subject displayed 5/8/3 and was built as 4/8/4). Both sides now call the same
// functions here, so "what it says" and "what it picks" cannot drift apart.
//
// Everything below uses the largest-remainder method: floor every share, then
// hand the leftover units to whoever was rounded down hardest. The parts always
// sum exactly to the requested total, which plain rounding does not guarantee
// (33 split 30/50/20 rounds to 10+17+7 = 34).

export interface DifficultyPct { easy: number; medium: number; hard: number }
export interface DifficultySplit { easy: number; medium: number; hard: number }

/**
 * Split `target` across `weights` proportionally, honouring optional per-slot
 * caps. Guarantees `sum(result) === min(target, sum(caps))` and never exceeds a
 * cap. Weights of zero receive nothing unless a top-up is needed to reach the
 * target and nothing else has room.
 */
export function distributeByWeight(weights: number[], target: number, caps?: number[]): number[] {
  const n = weights.length;
  const out: number[] = new Array(n).fill(0);
  if (n === 0) return out;

  const capOf = (i: number): number =>
    caps && caps[i] !== undefined && caps[i] !== null
      ? Math.max(0, Math.floor(caps[i]))
      : Number.MAX_SAFE_INTEGER;

  let totalCapacity = 0;
  for (let i = 0; i < n; i++) totalCapacity += capOf(i);

  const want = Math.max(0, Math.min(Math.floor(target) || 0, totalCapacity));
  if (want === 0) return out;

  const weightSum = weights.reduce((a, w) => a + Math.max(0, w || 0), 0);
  let placed = 0;

  if (weightSum > 0) {
    const remainders: { i: number; frac: number }[] = [];
    for (let i = 0; i < n; i++) {
      const ideal = (Math.max(0, weights[i] || 0) / weightSum) * want;
      const base = Math.min(Math.floor(ideal), capOf(i));
      out[i] = base;
      placed += base;
      remainders.push({ i, frac: ideal - Math.floor(ideal) });
    }
    // Largest remainder first; ties go to the heavier slot so the result is
    // deterministic rather than dependent on sort stability.
    remainders.sort((a, b) => b.frac - a.frac || (weights[b.i] || 0) - (weights[a.i] || 0));
    for (const r of remainders) {
      if (placed >= want) break;
      if (out[r.i] < capOf(r.i)) { out[r.i]++; placed++; }
    }
  }

  // Rounding down and per-slot caps can both strand units. Hand whatever is
  // left to any slot with room, heaviest first — this is what stops the old
  // auto-fill from quietly landing under target.
  if (placed < want) {
    const order = weights
      .map((w, i) => ({ w: Math.max(0, w || 0), i }))
      .sort((a, b) => b.w - a.w);
    let progressed = true;
    while (placed < want && progressed) {
      progressed = false;
      for (const { i } of order) {
        if (placed >= want) break;
        if (out[i] < capOf(i)) { out[i]++; placed++; progressed = true; }
      }
    }
  }

  return out;
}

/**
 * Split `total` questions into easy/medium/hard using percentage weights.
 * The three parts always sum to exactly `total`.
 */
export function splitByDifficulty(total: number, pct: DifficultyPct): DifficultySplit {
  const [easy, medium, hard] = distributeByWeight(
    [Math.max(0, pct.easy || 0), Math.max(0, pct.medium || 0), Math.max(0, pct.hard || 0)],
    total
  );
  return { easy, medium, hard };
}

/**
 * Normalise three difficulty percentages so they sum to 100 without any band
 * being silently forced to equal another. Used by the slider, which previously
 * derived medium from easy and hard and so could never reproduce its own
 * presets (below the midpoint it always produced medium === hard).
 */
export function normaliseDifficulty(pct: DifficultyPct): DifficultyPct {
  const easy = Math.max(0, pct.easy || 0);
  const medium = Math.max(0, pct.medium || 0);
  const hard = Math.max(0, pct.hard || 0);
  const sum = easy + medium + hard;
  if (sum === 0) return { easy: 34, medium: 33, hard: 33 };
  const [e, m, h] = distributeByWeight([easy, medium, hard], 100);
  return { easy: e, medium: m, hard: h };
}
